import { type JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import mergeRefs from 'merge-refs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { ComposerEditorControls } from '@/components/blocks/Thread/composer-editor-controls';
import { useComposerEditor } from '@/components/blocks/Thread/useComposerEditor';
import { useRegisterSendChat } from '@/components/blocks/Thread/useRegisterSendChat';
import { Button, XStack, YStack } from '@/components/ui';
import { classList } from '@/utils/cn';

import { MessageQuotes } from './MessageQuotes';

type UserMessageEditorProps = {
  initialBody: string;
  quotes: string[];
  isCompact?: boolean;
  isSendDisabled: boolean;
  onCancel: () => void;
  onSubmit: (body: string) => void;
};

export const UserMessageEditor = ({
  initialBody,
  quotes,
  isCompact,
  isSendDisabled,
  onCancel,
  onSubmit,
}: UserMessageEditorProps) => {
  const [draft, setDraft] = useState<{ text: string; document?: JSONContent }>({
    text: initialBody,
  });
  const inputRef = useRef<HTMLElement>(undefined);
  const canSubmit = !isSendDisabled && draft.text.trim().length > 0;
  const submit = () => {
    if (canSubmit) onSubmit(draft.text);
  };

  const sendHotkeyRef = useRegisterSendChat(inputRef, {
    isDisabled: !canSubmit,
    canSendNow: false,
    hasQueuedMessage: false,
    onSendChat: submit,
    onSendNow: submit,
  });
  const cancelHotkeyRef = useHotkeys('esc', onCancel, {
    preventDefault: true,
    enableOnContentEditable: true,
    enableOnFormTags: true,
  });
  const mergedInputRef = useMemo(
    () => mergeRefs(inputRef, sendHotkeyRef, cancelHotkeyRef),
    [sendHotkeyRef, cancelHotkeyRef]
  ) as (element: HTMLElement | null) => void;

  const editor = useComposerEditor({
    value: draft.text,
    document: draft.document,
    ariaLabel: 'Edit message',
    onChange: (text, document) => setDraft({ text, document }),
    onPaste: () => false,
    inputRef: mergedInputRef,
  });

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(initialBody);
    editor.commands.focus('end');
  }, [editor, initialBody]);

  return (
    <YStack
      className={classList(
        'w-full gap-3 rounded-xl px-3.5 py-2.5',
        isCompact ? 'bg-grayA-3' : 'bg-surface-hover'
      )}
    >
      <YStack className="gap-2">
        {quotes.length > 0 && <MessageQuotes quotes={quotes} />}
        <YStack className="max-h-[20rem] overflow-y-auto text-base text-foreground">
          <ComposerEditorControls editor={editor}>
            <EditorContent editor={editor} />
          </ComposerEditorControls>
        </YStack>
      </YStack>
      <XStack className="justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={!canSubmit} onClick={submit}>
          Send
        </Button>
      </XStack>
    </YStack>
  );
};
